import { Component, OnInit, Inject, ViewChild, OnDestroy, AfterViewInit, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { select } from '@angular-redux/store';
import { Observable, Subscription, throwError } from 'rxjs';
import { Poi } from '../../core/poi/poi.model';
import { PoiActionService } from '../../core/poi/poi.action';
import { ApiService } from 'src/app/services/api.service';
import { BreadcrumbService } from 'angular-crumbs';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { catchError, tap, first, mergeMap } from 'rxjs/operators';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Themes } from 'src/app/core/themes/themes.model';
import { GeoPos } from 'src/app/core/geographicalpos/geographicalpos.model';
import { AddAttachComponent } from 'src/app/shared/add-attach/add-attach.component';
import { FailRequestComponent } from 'src/app/shared/fail-request/fail-request.component';
import { SuccessRequestComponent } from 'src/app/shared/success-request/success-request.component';
import { User } from 'src/app/core/users/users.model';
import { Attachments } from 'src/app/core/attachments/attachments.model';
import { AddAvatarComponent } from 'src/app/shared/add-avatar/add-avatar.component';
import { AddVideoComponent } from 'src/app/shared/add-video/add-video.component';
import { CrudAlertService } from 'src/app/services/crud-alert.service';
import { CrudAlertComponent } from 'src/app/shared/crud-alert/crud-alert.component';

export interface DialogData {
  id: any;
  poi_name: object;
  current_lang: string;
}

@Component({
  selector: 'app-appr-poi-singolo',
  templateUrl: './appr-poi-singolo.component.html',
  styleUrls: ['./appr-poi-singolo.component.scss']
})
export class ApprPoiSingoloComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('map', {static: false}) mapElement: ElementRef;
  @ViewChild('avatar') avatar: any;
  @ViewChild('titleAudio') title_audio: any;
  @ViewChild('themesSelection') themes_selection: any;
  @ViewChild('picSelection') pic_selection: any;
  @ViewChild('audioSelection') audio_selection: any;
  @ViewChild('docSelection') doc_selection: any;
  @ViewChild('videoSelection') video_selection: any;

  @select(['poi', 'current'])
  poi_current$: Observable<Poi>;
  poi_current: Poi;
  @select(['theme', 'list'])
  themes_list$: Observable<any>;
  themes_list: Themes[] = [];
  @select(['geopos', 'list'])
  geopos_list$: Observable<any>;
  geopos_list: GeoPos[] = [];
  @select(['logged', 'logged'])
  user_logged$: Observable<any>;
  user_logged: User;

  subscriptions: Subscription[] = [];
  current_lang = 'it';

  current_id: any;
  current_name: object;

  actual_att_selection = [];
  loading = false;

  geopos_center = {
    'Cimbri dell\'Altopiano dei Sette Comuni': {lat: 45.891109, lon: 11.547864},
    'Friulani in Veneto': {lat: 45.77391749995606, lon: 12.937330012651866},
    'Italiani in Slovenia': {lat: 45.50906685999279, lon: 13.780505952515352 },
    'Ladini del Comelico': {lat: 46.602049522923586, lon: 12.550061946245616},
    'Sloveni in Italia': {lat: 45.956334177758094, lon: 13.467896260697547 },
    'Friulani in Friuli Venezia Giulia': {lat: 45.956334177758094, lon: 13.467896260697547 }
  };

  generic_center = {
    lat: Object.keys(this.geopos_center).map((elem) =>
      this.geopos_center[elem].lat).reduce((a, b) => a + b, 0) / Object.keys(this.geopos_center).length,
    lon: Object.keys(this.geopos_center).map((elem) =>
      this.geopos_center[elem].lon).reduce((a, b) => a + b, 0) / Object.keys(this.geopos_center).length,
  };

  map: google.maps.Map;
  infoWindow: google.maps.InfoWindow;

  clickMapText: string = '';

  constructor(private titleService: Title,
              private route: Router,
              private activateroute: ActivatedRoute,
              private apiservice: ApiService,
              private poiAction: PoiActionService,
              private breadcrumbservice: BreadcrumbService,
              private translate: TranslateService,
              public dialog: MatDialog,
              private crudAlertService: CrudAlertService) {
    this.translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.get('breadcrumb.admin_single_poi').subscribe((res: string) => {
      this.titleService.setTitle(res + ' - PRIMIS');
    });
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.changeLangFunction();
    });
  }

  ngOnInit(): void {
    this.user_logged$.pipe(
      first(),
      mergeMap((user) => {
        this.user_logged = user;
        return this.poi_current$.pipe(first());
      }),
      tap((poi) => {
        this.poi_current = poi;
        const geoposList = this.user_logged.geopos.map(({id}) => id);
        if (!geoposList.includes(this.poi_current.geopos[0].id)) {
          this.routerFunction('/');
        }
        this.current_id = this.poi_current.id;
        this.current_name = this.poi_current.poi_name;
        this.changeLangFunction();
      })
    ).subscribe();

    this.subscriptions.push(
      this.themes_list$.subscribe(list => {
        this.themes_list = [...list];
      }),
      this.geopos_list$.subscribe(list => {
        this.geopos_list = [...list];
      }),
    );
  }

  ngAfterViewInit(): void {
    this.mapInitializer();
  }

  ngOnDestroy(): void {
    this.poiAction.setPoiCurrent(null);
    this.subscriptions.forEach(x => x.unsubscribe());
  }

  mapInitializer(): void {
    let centerLat = this.generic_center.lat;
    let centerLon = this.generic_center.lon;
    if (!!this.poi_current.coords?.lat && !!this.poi_current.coords?.lon) {
      centerLat = this.poi_current.coords.lat;
      centerLon = this.poi_current.coords.lon;
    } else if (this.poi_current.geopos.length > 0) {
      centerLat = this.geopos_center[this.poi_current.geopos[0].name.it].lat;
      centerLon = this.geopos_center[this.poi_current.geopos[0].name.it].lon;
    }
    const mapProperties: google.maps.MapOptions = {
      center: new google.maps.LatLng(centerLat, centerLon),
      zoom: 11,
      restriction: {
        latLngBounds: {
          north: 47,
          south: 45,
          west: 10.9,
          east: 14.6
        },
        strictBounds: false,
      },
      mapTypeId: google.maps.MapTypeId.ROADMAP,
    };
    this.map = new google.maps.Map(this.mapElement.nativeElement, mapProperties);

    // Create the initial InfoWindow.
    this.infoWindow = new google.maps.InfoWindow({
      content: this.clickMapText,
      position: { lat: centerLat, lng: centerLon },
    });
    if (!!this.poi_current.coords?.lat && !!this.poi_current.coords?.lon) {
      const contentString =
        '<div id="content">' +
        '<div id="siteNotice"></div>' +
        '<div id="bodyContent">' +
        '<p><b>Lat</b>: ' + centerLat.toString().slice(0, 7) + '</p>' +
        '<p><b>Lng</b>: ' + centerLon.toString().slice(0, 7) + '</p>' +
        '</div>' +
        '</div>';
      this.infoWindow.setContent(contentString);
    }
    this.infoWindow.open(this.map);

    // Configure the click listener.
    this.map.addListener('click', (mapsMouseEvent) => {
      // Close the current InfoWindow.
      this.infoWindow.close();

      // Create a new InfoWindow.
      this.infoWindow = new google.maps.InfoWindow({
        position: mapsMouseEvent.latLng,
      });
      const lat = mapsMouseEvent.latLng.toJSON().lat;
      const lon = mapsMouseEvent.latLng.toJSON().lng;
      const contentString =
        '<div id="content">' +
        '<div id="siteNotice"></div>' +
        '<div id="bodyContent">' +
        '<p><b>Lat</b>: ' + lat.toString().slice(0, 7) + '</p>' +
        '<p><b>Lng</b>: ' + lon.toString().slice(0, 7) + '</p>' +
        '</div>' +
        '</div>';
      this.infoWindow.setContent(contentString);
      this.poi_current.coords = {lat, lon};
      this.infoWindow.open(this.map);
    });
  }

  updateCoords() {
    // Close the current InfoWindow.
    this.infoWindow.close();

    if (!!this.poi_current.coords?.lat && !!this.poi_current.coords?.lon) {
      const lat = this.poi_current.coords?.lat;
      const lon = this.poi_current.coords?.lon;
      // Create a new InfoWindow.
      this.infoWindow = new google.maps.InfoWindow({
        position: {
          lat,
          lng: lon
        },
      });
      const contentString =
        '<div id="content">' +
        '<div id="siteNotice"></div>' +
        '<div id="bodyContent">' +
        '<p><b>Lat</b>: ' + lat.toString().slice(0, 7) + '</p>' +
        '<p><b>Lng</b>: ' + lon.toString().slice(0, 7) + '</p>' +
        '</div>' +
        '</div>';
      this.infoWindow.setContent(contentString);
      this.poi_current.coords = {lat, lon};
      this.infoWindow.open(this.map);
    }
  }

  checkGeopos(geopos) {
    return geopos.id === this.poi_current.geopos[0].id;
  }

  elemDelete() {
    this.dialog.open(DeleteApprPoiDialogContent, {
      width: '450px',
      data: {
        id: this.current_id,
        poi_name: this.current_name,
        current_lang: this.current_lang
      }
    });
  }

  themesSelectionFunction() {
    this.poi_current.themes = this.themes_selection.selectedOptions.selected.map((opt) => opt._value);
  }

  attachmentSelectionFunction() {
    const values_selected = [];
    if (this.pic_selection !== undefined) {
      for (const opt of this.pic_selection.selectedOptions.selected) {
        values_selected.push(opt._value);
      }
    }
    if (this.audio_selection !== undefined) {
      for (const opt of this.audio_selection.selectedOptions.selected) {
        values_selected.push(opt._value);
      }
    }
    if (this.doc_selection !== undefined) {
      for (const opt of this.doc_selection.selectedOptions.selected) {
        values_selected.push(opt._value);
      }
    }
    if (this.video_selection !== undefined) {
      for (const opt of this.video_selection.selectedOptions.selected) {
        values_selected.push(opt._value);
      }
    }
    if (this.avatar !== undefined) {
      for (const opt of this.avatar.selectedOptions.selected) {
        values_selected.push(opt._value);
      }
    }

    this.actual_att_selection = [...values_selected];
  }

  addAtt() {
    const dialogRef = this.dialog.open(AddAttachComponent, {
      width: '450px',
      data: {
        current_lang: this.current_lang
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.poi_current.attachments.push(result);
      }
    });
  }

  addVid() {
    const dialogRef = this.dialog.open(AddVideoComponent, {
      width: '450px',
      data: {
        current_lang: this.current_lang
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.poi_current.attachments.push(result);
      }
    });
  }

  addAva() {
    const dialogRef = this.dialog.open(AddAvatarComponent, {
      width: '450px',
      data: {
        current_lang: this.current_lang
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const avatarIdx = this.poi_current.attachments.findIndex(({avatar}) => avatar);
        if (avatarIdx < 0) {
          this.poi_current.attachments.push(result);
        } else {
          this.poi_current.attachments.splice(avatarIdx, 1, result);
        }
      }
    });
  }

  remAtt() {
    for (const att of this.actual_att_selection) {
      let index_of_att = this.poi_current.attachments.indexOf(att);
      if (index_of_att > -1) {
        this.poi_current.attachments.splice(index_of_att, 1);
      }
    }
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
    this.titleService.setTitle(this.poi_current.poi_name[this.current_lang] + ' - PRIMIS');
    this.breadcrumbservice.changeBreadcrumb(this.activateroute.snapshot, this.poi_current.poi_name[this.current_lang]);
    this.translate.get('admin_poi_to_approve.sections.click_map').pipe(
      first(),
      tap((res: string) => {
        this.clickMapText = res;
        if (!!this.mapElement) {
          this.mapInitializer();
        }
      }),
    ).subscribe();
  }

  routerFunction(path_to_new_page) {
    this.route.navigate([path_to_new_page]);
  }

  checkAttachments(att_list: Attachments[], att_type: string) {
    for (const att of att_list) {
      if ((att.file_type === att_type && att_type !== 'picture') ||
        (att.file_type === att_type && att_type === 'picture' && !att.avatar) ||
        (att_type === 'avatar' && att.avatar)) {
        return true;
      }
    }
    return false;
  }

  singleTypeAttachments(att_list: Attachments[], att_type: string) {
    let single_type_list = [];
    for (const att of att_list) {
      if ((att.file_type === att_type && att_type !== 'picture') ||
        (att.file_type === att_type && att_type === 'picture' && !att.avatar) ||
        (att_type === 'avatar' && att.avatar)) {
        single_type_list.push(att);
      }
    }
    return single_type_list;
  }

  checkDict(array_of_dict, elem_id) {
    let list_of_ids = array_of_dict.map((value) => value.id);
    return list_of_ids.includes(elem_id);
  }

  approvePoi() {
    this.loading = true;
    const poiValidityErrors: string[] = this.crudAlertService.checkPoiValidity(this.poi_current);
    if (poiValidityErrors === null) {
      this.apiservice.approvePoi(this.poi_current).pipe(
        tap((x: any) => {
          if (x && !x.error) {
            const approvedPoi: Poi = {
              ...x.poi,
              approved: true
            };
            if (approvedPoi.id === x.old) {
              this.poiAction.addOrUpdatePoi(approvedPoi);
            } else {
              this.poiAction.addOrUpdatePoi(approvedPoi);
              this.poiAction.deletePoi(x.old);
            }
            this.loading = false;
            const dialogRef = this.dialog.open(SuccessRequestComponent, {
              width: '450px'
            });
            dialogRef.afterClosed().subscribe(() => this.routerFunction('/lista-da-approvare'));
          }
        }),
        catchError(error => {
          this.loading = false;
          this.dialog.open(FailRequestComponent, {
            width: '450px'
          });
          return throwError(error);
        })
      ).subscribe();
    } else if (poiValidityErrors?.length > 0) {
      this.loading = false;
      this.dialog.open(CrudAlertComponent, {
        width: '450px',
        data: {
          alertFields: poiValidityErrors
        }
      });
    }
  }

  titleAudioDisabled() {
    return this.poi_current.attachments.some(({title_audio}) => title_audio === true);
  }

  titleAudioSelection() {
    const option_selected = this.title_audio.selectedOptions.selected[0]._value;
    const index = this.poi_current.attachments.indexOf(option_selected);
    this.poi_current.attachments[index].title_audio = true;
  }
}

@Component({
  selector: 'deleteapprpoi-dialog-content',
  templateUrl: 'deleteapprpoi-dialog-content.html',
  styleUrls: ['./appr-poi-singolo.component.scss']
})
export class DeleteApprPoiDialogContent {
  loading = false;

  constructor(
    public dialogRef: MatDialogRef<DeleteApprPoiDialogContent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private apiservice: ApiService,
    private route: Router,
    public dialog: MatDialog,
    private poiAction: PoiActionService) {}

  onNoClick(): void {
    this.dialogRef.close();
  }

  unapprovePoi() {
    this.loading = true;
    let poi_id = this.data.id;
    this.apiservice.unapprovePoi(poi_id).pipe(
      tap((x: any) => {
        this.poiAction.deletePoi(poi_id);
        this.loading = false;
        this.dialogRef.close();
        if (x && !x.error) {
          const dialogRef = this.dialog.open(SuccessRequestComponent, {
            width: '450px'
          });
          dialogRef.afterClosed().subscribe(() => this.routerFunction('/lista-da-approvare'));
        }
      }),
      catchError(error => {
        this.loading = false;
        this.dialog.open(FailRequestComponent, {
          width: '450px'
        });
        return throwError(error);
      })
    ).subscribe();
  }

  routerFunction(path_to_new_page) {
    this.route.navigate([path_to_new_page]);
  }
}
